import { customFetch } from '@agos/api-client'
import type { Branding } from '@agos/api-client/model'
import { useEffect, useState } from 'react'

/**
 * Per-franchise white-label branding, ported from legacy `lib/use-branding.ts`.
 *
 * Resolved by the backend from the request `Host` header, so a franchise on its
 * own domain gets its own name, logo, favicon and colours before anybody signs
 * in. On localhost no domain matches and the backend falls back to the first
 * active franchise, which is what makes the login screen look real in
 * development.
 */
// The shape used to be hand-written here, and it was two fields short of what the endpoint
// answers — `themeName` and `modules` were never declared. It comes from the backend now
// (BLOCKED #4); re-exported because callers import `Branding` from this module.
export type { Branding }

function applyBranding(branding: Branding): void {
  const root = document.documentElement

  if (branding.primaryColor) root.style.setProperty('--brand-primary', branding.primaryColor)
  if (branding.secondaryColor) root.style.setProperty('--brand-secondary', branding.secondaryColor)
  if (branding.displayName) document.title = branding.displayName

  if (branding.faviconUrl) {
    const existing = document.querySelector<HTMLLinkElement>("link[rel='icon']")
    const link = existing ?? document.createElement('link')
    link.rel = 'icon'
    link.href = branding.faviconUrl
    if (!existing) document.head.appendChild(link)
  }

  // Free-form overrides, e.g. { "--accent": "#ff0" }. Only custom properties
  // are applied — a key that is not one would be a stored value writing
  // arbitrary CSS.
  if (branding.theme && typeof branding.theme === 'object') {
    for (const [property, value] of Object.entries(branding.theme)) {
      if (typeof value === 'string' && property.startsWith('--')) {
        root.style.setProperty(property, value)
      }
    }
  }
}

export function useBranding(): { branding: Branding | null; isLoading: boolean } {
  const [branding, setBranding] = useState<Branding | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const resolved = await customFetch<Branding>('/api/v1/central/branding')
        if (cancelled) return
        setBranding(resolved)
        applyBranding(resolved)
      } catch {
        // No branding is survivable: the app falls back to its own name.
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  return { branding, isLoading }
}
